'use client';

/**
 * PlaceProvider — the one client-side store the shell reads.
 *
 * WHY IT HAD TO EXIST. The cart lived inside PublicMenuOrderingExperience, a
 * 2,612-line island mounted three levels below the chrome. So the chrome could
 * not badge it, the ribbon could not mention it, and no other customer page
 * could see it at all — a guest with four items chosen who tapped through to the
 * booking page found a screen that behaved as if their cart did not exist. Every
 * "not synced" symptom traces back to state that only one component could see.
 *
 * This is the seam that fixes it without rewriting the island: the island
 * *reports* its cart up (`useReportCart`), the shell *reads* it, and the two
 * never import each other. When the cart is eventually lifted out of the island
 * for real, only the reporter call changes.
 *
 * Everything the shell needs is here and nowhere else, so there is exactly one
 * copy of the guest's situation to keep in sync.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { resolvePlaceState, type PlaceState, type PlaceStateInput } from '@/lib/customer/place-state';

type CartSummary = { count: number; total: string | null };

/**
 * Actions that live inside the ordering island but need to be reachable from the
 * chrome above it.
 *
 * Same one-way pattern as the cart: the island owns the state and registers a
 * callback; the shell only calls it. Without this the island had to render its
 * OWN floating bar to expose them, which is how a guest at a table ended up with
 * a "Discover / Orders" pill sitting directly on top of the bottom rail,
 * covering two of its four tabs.
 */
export type PlaceActions = {
  openDiscover?: () => void;
  openOrders?: () => void;
  /** Opens the cart sheet. Absent when this branch cannot take an order. */
  openCart?: () => void;
};

/**
 * Which slots an unmount should clear. `undefined` slots in a merge mean "keep",
 * so clearing has to be explicit about the keys it owns.
 */
type ActionRegistryKeys = {
  openDiscover?: true;
  openOrders?: true;
  openCart?: true;
};

type PlaceContextValue = {
  /** Route root of this place, e.g. `/m/sankofa-diner-osu`. */
  basePath: string;
  cart: CartSummary;
  reportCart: (summary: CartSummary) => void;
  actions: PlaceActions;
  registerActions: (actions: PlaceActions) => void;
  unregisterActions: (keys: ActionRegistryKeys) => void;
  state: PlaceState;
};

const PlaceContext = createContext<PlaceContextValue | null>(null);

export function PlaceProvider({
  basePath,
  /**
   * Everything the shell's state depends on except the cart, which the ordering
   * island reports in. Passing the rest down means the server-resolved facts
   * (branch hours, session, live order) have a single owner.
   */
  situation,
  children,
}: {
  basePath: string;
  situation: Omit<PlaceStateInput, 'cartCount' | 'cartTotal'>;
  children: ReactNode;
}) {
  const [cart, setCart] = useState<CartSummary>({ count: 0, total: null });
  const [actions, setActions] = useState<PlaceActions>({});

  const registerActions = useCallback((next: PlaceActions) => {
    // Merge, with `undefined` meaning "not this component's concern, keep
    // whatever is already there". The ordering island (which owns the real
    // openCart) and the page above it (which provides a browse-mode fallback)
    // both register the same slot, so the LAST MOUNTED owner wins and an
    // unmount must reveal the previous owner rather than wiping the slot.
    setActions((prev) => {
      const merged: PlaceActions = {
        openDiscover: next.openDiscover ?? prev.openDiscover,
        openOrders: next.openOrders ?? prev.openOrders,
        openCart: next.openCart ?? prev.openCart,
      };
      return prev.openDiscover === merged.openDiscover &&
        prev.openOrders === merged.openOrders &&
        prev.openCart === merged.openCart
        ? prev
        : merged;
    });
  }, []);

  /**
   * Clear only the listed slots (used on unmount so a component's handlers do
   * not linger — and so the shell falls back to whoever registered before it).
   */
  const unregisterActions = useCallback((keys: ActionRegistryKeys) => {
    if (!keys.openDiscover && !keys.openOrders && !keys.openCart) return;
    setActions((prev) => ({
      openDiscover: keys.openDiscover ? undefined : prev.openDiscover,
      openOrders: keys.openOrders ? undefined : prev.openOrders,
      openCart: keys.openCart ? undefined : prev.openCart,
    }));
  }, []);

  const reportCart = useCallback((summary: CartSummary) => {
    // Guard against the island re-reporting an identical summary on every
    // render, which would re-render the whole shell on each keystroke in a
    // quantity field.
    setCart((prev) =>
      prev.count === summary.count && prev.total === summary.total ? prev : summary,
    );
  }, []);

  const state = useMemo(
    () => resolvePlaceState({ ...situation, cartCount: cart.count, cartTotal: cart.total }),
    // `situation` is rebuilt by the parent each render, so depend on the fields
    // that actually change rather than on its identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      situation.branch,
      situation.session,
      situation.order,
      situation.mode,
      situation.now,
      cart.count,
      cart.total,
    ],
  );

  const value = useMemo<PlaceContextValue>(
    () => ({ basePath, cart, reportCart, actions, registerActions, unregisterActions, state }),
    [basePath, cart, reportCart, actions, registerActions, unregisterActions, state],
  );

  return <PlaceContext.Provider value={value}>{children}</PlaceContext.Provider>;
}

/**
 * Read the place. Throws rather than returning a default: a customer surface
 * rendering outside the shell is a routing mistake, and a silent empty state
 * would hide it until a guest hit it.
 */
export function usePlace(): PlaceContextValue {
  const value = useContext(PlaceContext);
  if (!value) throw new Error('usePlace must be used inside a PlaceProvider');
  return value;
}

/**
 * Optional read, for components that legitimately render both inside and
 * outside a place (an item card reused on the dashboard, say).
 */
export function usePlaceOptional(): PlaceContextValue | null {
  return useContext(PlaceContext);
}

/**
 * Registers the island's sheet-openers with the shell, and clears only its own
 * slots on unmount. Stable references required — pass `useCallback`-wrapped
 * handlers, or this re-registers on every render. No-ops outside a place.
 */
export function useRegisterPlaceActions(actions: PlaceActions) {
  const place = useContext(PlaceContext);
  const register = place?.registerActions;
  const unregister = place?.unregisterActions;
  const { openDiscover, openOrders, openCart } = actions;

  const ownedKeys = useMemo<ActionRegistryKeys>(
    () => ({
      ...(openDiscover ? { openDiscover: true as const } : {}),
      ...(openOrders ? { openOrders: true as const } : {}),
      ...(openCart ? { openCart: true as const } : {}),
    }),
    [openDiscover, openOrders, openCart],
  );

  useEffect(() => {
    register?.({ openDiscover, openOrders, openCart });
    return () => unregister?.(ownedKeys);
  }, [register, unregister, openDiscover, openOrders, openCart, ownedKeys]);
}

/**
 * Called by whatever currently owns the cart. Safe to call outside a place —
 * the ordering island is also mounted on the kiosk and preview routes, which
 * have no shell to report to.
 */
export function useReportCart(summary: CartSummary) {
  const place = useContext(PlaceContext);
  const report = place?.reportCart;
  const { count, total } = summary;

  useEffect(() => {
    report?.({ count, total });
  }, [report, count, total]);
}
